import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Book, ChevronRight, Search, Edit } from 'lucide-react';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API_URL = window.__BACKEND_URL;

const CATEGORIES = [
  { id: 'overview', label: 'Overview', icon: '📖' },
  { id: 'projects', label: 'Projects', icon: '📁' },
  { id: 'channels', label: 'Channels', icon: '📺' },
  { id: 'live_ingest', label: 'Live Ingest', icon: '🔴' },
  { id: 'clip_engine', label: 'Clip Engine', icon: '✂️' },
  { id: 'storage', label: 'Storage', icon: '💾' },
  { id: 'dashboards', label: 'Dashboards', icon: '📊' },
  { id: 'automation', label: 'Automation', icon: '🤖' },
  { id: 'sop', label: 'SOPs', icon: '📋' },
  { id: 'troubleshooting', label: 'Troubleshooting', icon: '🔧' }
];

export default function FounderManual() {
  const { slug } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadPages();
  }, [user]);

  useEffect(() => {
    if (slug && pages.length > 0) {
      const page = pages.find(p => p.slug === slug);
      if (page) {
        setCurrentPage(page);
        setSelectedCategory(page.category);
      }
    } else if (pages.length > 0 && !slug) {
      // Default to first page
      const firstPage = pages[0];
      navigate(`/admin/founder-manual/${firstPage.slug}`, { replace: true });
    }
  }, [slug, pages]);

  const loadPages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/admin/founder-manual/pages`,
        { withCredentials: true }
      );
      setPages(response.data);
    } catch (err) {
      console.error('Error loading pages:', err);
      toast.error('Failed to load founder manual');
    } finally {
      setLoading(false);
    }
  };

  const getPagesByCategory = (category) => {
    return pages.filter(p => p.category === category);
  };

  const filteredPages = searchQuery
    ? pages.filter(p => 
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.body_markdown.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : getPagesByCategory(selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="p-8 text-center">
        <Book className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2">No Manual Pages Found</h2>
        <p className="text-gray-400 mb-6">Seed the default pages to get started</p>
        <button
          onClick={async () => {
            try {
              await axios.post(
                `${API_URL}/api/admin/founder-manual/seed`,
                {},
                { withCredentials: true }
              );
              toast.success('Manual pages seeded successfully');
              loadPages();
            } catch (err) {
              toast.error('Failed to seed pages');
            }
          }}
          className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
        >
          Seed Default Pages
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen flex">
      {/* Sidebar */}
      <div className="w-80 bg-[#1a1d23] border-r border-gray-800 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center gap-3 mb-4">
            <Book className="w-6 h-6 text-cyan-500" />
            <h1 className="text-xl font-bold text-white">Founder Manual</h1>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search pages..."
              className="w-full pl-10 pr-4 py-2 bg-[#121417] border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-600"
            />
          </div>
        </div>

        {/* Categories & Pages */}
        <div className="flex-1 overflow-y-auto">
          {searchQuery ? (
            <div className="p-4">
              <div className="text-sm text-gray-400 mb-2">
                {filteredPages.length} results
              </div>
              {filteredPages.map(page => (
                <button
                  key={page.id}
                  onClick={() => navigate(`/admin/founder-manual/${page.slug}`)}
                  className={`w-full text-left px-3 py-2 rounded-lg mb-1 transition-colors ${
                    currentPage?.slug === page.slug
                      ? 'bg-cyan-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <div className="text-sm font-medium">{page.title}</div>
                  <div className="text-xs text-gray-400">{page.category}</div>
                </button>
              ))}
            </div>
          ) : (
            CATEGORIES.map(category => {
              const categoryPages = getPagesByCategory(category.id);
              if (categoryPages.length === 0) return null;
              
              return (
                <div key={category.id}>
                  <button
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full px-6 py-3 flex items-center justify-between hover:bg-gray-800 transition-colors ${
                      selectedCategory === category.id ? 'bg-gray-800' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{category.icon}</span>
                      <span className="text-white font-medium">{category.label}</span>
                    </div>
                    <span className="text-xs text-gray-500">{categoryPages.length}</span>
                  </button>
                  
                  {selectedCategory === category.id && (
                    <div className="bg-[#121417]">
                      {categoryPages.map(page => (
                        <button
                          key={page.id}
                          onClick={() => navigate(`/admin/founder-manual/${page.slug}`)}
                          className={`w-full text-left px-10 py-2 flex items-center gap-2 transition-colors ${
                            currentPage?.slug === page.slug
                              ? 'bg-cyan-600 text-white'
                              : 'text-gray-300 hover:bg-gray-800'
                          }`}
                        >
                          <ChevronRight className="w-3 h-3" />
                          <span className="text-sm">{page.title}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col bg-[#121417]">
        {currentPage ? (
          <>
            {/* Page Header */}
            <div className="p-8 border-b border-gray-800 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">{currentPage.title}</h1>
                <div className="flex items-center gap-3 text-sm text-gray-400">
                  <span>Category: {currentPage.category}</span>
                  <span>•</span>
                  <span>Last updated: {new Date(currentPage.updated_at).toLocaleDateString()}</span>
                  {currentPage.is_system_page && (
                    <>
                      <span>•</span>
                      <span className="text-cyan-400">System Page</span>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={() => navigate(`/admin/founder-manual/edit/${currentPage.slug}`)}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors inline-flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
            </div>

            {/* Page Content */}
            <div className="flex-1 overflow-y-auto p-8">
              <div className="max-w-4xl mx-auto prose prose-invert prose-cyan">
                <ReactMarkdown>{currentPage.body_markdown}</ReactMarkdown>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            Select a page from the sidebar
          </div>
        )}
      </div>
    </div>
  );
}
